import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface VideoPreviewProps {
  file: File;
  onRemove: () => void;
}

const PreviewContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background-color: #1F1A29;
`;

const VideoElement = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  opacity: 0;
  transition: opacity 0.2s;
  
  ${PreviewContainer}:hover & {
    opacity: 1;
  }
`;

const OverlayTop = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const OverlayBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RemoveButton = styled.button`
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 16px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

const FileInfo = styled.div`
  color: white;
  font-size: 12px;
`;

const PlayButton = styled.button`
  background-color: rgba(147, 51, 234, 0.8);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(147, 51, 234, 1);
  }
`;

const VideoPreview: React.FC<VideoPreviewProps> = ({ file, onRemove }) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  return (
    <PreviewContainer>
      <VideoElement 
        ref={videoRef}
        src={videoUrl} 
        controls={false}
        onEnded={() => setIsPlaying(false)}
      />
      <PreviewOverlay>
        <OverlayTop>
          <RemoveButton onClick={onRemove}>×</RemoveButton>
        </OverlayTop>
        <OverlayBottom>
          <FileInfo>
            {file.name} ({formatFileSize(file.size)})
          </FileInfo>
          <PlayButton onClick={togglePlay}>
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="2" width="3" height="12" rx="1" fill="white"/>
                <rect x="10" y="2" width="3" height="12" rx="1" fill="white"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 3.5V12.5L13 8L4 3.5Z" fill="white"/>
              </svg>
            )}
          </PlayButton>
        </OverlayBottom>
      </PreviewOverlay>
    </PreviewContainer>
  );
};

export default VideoPreview;
